import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { takeUntil } from 'rxjs';
import { SweetAlertOptions } from 'sweetalert2';

import { Company, EventTypes, List, Project } from '@site-mate/dashpivot-shared-library';

import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { EventsService } from 'app/shared/service/events/events.service';
import { ToastrService } from 'app/shared/service/toastr.service';

import { BulkApplyListRowStyleModal } from '../bulk-apply-list-row-style/bulk-apply-list-row-style-modal.component';
import { ListItemSelectionService } from '../list-item-selection.service';
import { ListOverwriteWarningComponent } from '../list-overwrite-warning/list-overwrite-warning.component';
import { ListsService } from '../lists.service';

@Component({
  selector: 'cc-list-detail-tool-bar',
  templateUrl: './list-detail-tool-bar.component.html',
  styleUrls: ['./list-detail-tool-bar.component.scss', '../../../styles/components/tool-bar.component.scss'],
})
export class ListDetailToolBarComponent implements OnChanges {
  @Input() list: List;
  @Input() loading = false;
  @Input() updating = false;
  @Input() editExisting = false;
  @Input() listIsDeployed = false;
  @Input() canAddNewItem = false;
  @Input() folder: {
    hierarchy: Exclude<AppHierarchy, 'team'>;
    project?: Project;
    company?: Company;
  };
  @Input() triggerUploadListCSV: EventEmitter<void>;
  @Output() readonly listNameUpdated = new EventEmitter<string>();
  @Output() readonly addListItemStarted = new EventEmitter<void>();
  @Output() readonly listUploaded = new EventEmitter<{ fileList: FileList; list: List }>();
  @Output() readonly listDeployedViaCsv = new EventEmitter<{ fileList: FileList; list: List }>();
  @Output() readonly deleteItems = new EventEmitter<string[]>();
  @Output() readonly listItemStylesUpdated = new EventEmitter<{
    fullList: List;
  }>();
  isEscPressed = false;

  private unsubscribe = new EventEmitter<void>();
  selectedItemsSize = 0;

  @ViewChild('fileUploadInput') fileUploadInput: ElementRef<HTMLInputElement>;
  @ViewChild('listNameInput') listNameInput: ElementRef<HTMLInputElement>;

  ngOnInit() {
    this.triggerUploadListCSV?.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.uploadList();
    });

    this.listItemSelectionService.selectedItems$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((selectedItems) => {
        this.selectedItemsSize = selectedItems.size;
      });
  }

  constructor(
    private readonly modal: NgbModal,
    private readonly listItemSelectionService: ListItemSelectionService,
    private readonly confirmService: ConfirmService,
    private readonly eventsService: EventsService,
    private readonly listsService: ListsService,
    private readonly toastrService: ToastrService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.list && this.listNameInput) {
      this.updateInputSize(this.listNameInput.nativeElement, changes.list.currentValue.name);
    }
  }

  downloadList() {
    const newLine = '\n';
    const formattedItems = this.list.items.map((item) => String(item.value)).join(newLine);
    saveAs(new Blob([formattedItems]), `${this.list.name}.csv`);

    this.eventsService.trackEvent(EventTypes.ListItemActionApplied, { Context: 'Downloaded To CSV' });
  }

  uploadList() {
    return this.list.items.length ? this.openWarningModal() : this.openFileUpload();
  }

  private openWarningModal() {
    const modalRef = this.createWarningModal();
    this.setUpModalInstance(modalRef);

    modalRef.result
      .then(() => {
        this.openFileUpload();
      })
      .catch(() => {
        // no-op
      });
  }

  private createWarningModal(): NgbModalRef {
    return this.modal.open(ListOverwriteWarningComponent, { windowClass: 'sm-flex sm-items-center' });
  }

  private setUpModalInstance(modalRef: NgbModalRef) {
    const instance: ListOverwriteWarningComponent = modalRef.componentInstance;
    instance.deployedList = this.listIsDeployed;
  }

  private openFileUpload() {
    this.fileUploadInput.nativeElement.click();
  }

  onFileUpload(fileList: FileList) {
    if (this.listIsDeployed) {
      this.listDeployedViaCsv.emit({ list: this.list, fileList });
    } else {
      this.listUploaded.emit({ list: this.list, fileList });
    }

    this.eventsService.trackEvent(EventTypes.ListItemActionApplied, { Context: 'Uploaded From CSV' });
  }

  addListItem() {
    this.addListItemStarted.emit();
  }

  updateListName(name: string) {
    if (this.isEscPressed || name?.trim() === this.list?.name?.trim()) {
      this.isEscPressed = false;
      return;
    }

    this.listsService.updateList(this.folder, this.list.id, { name: name.trim() }).subscribe(() => {
      this.toastrService.successByKey('listNameUpdated');
      this.listNameInput.nativeElement.blur();
    });

    if (this.listNameInput) {
      this.listNameInput.nativeElement.scrollLeft = 0;
    }
  }

  updateInputSize(inputElement: HTMLInputElement, textValue?: string) {
    inputElement.parentElement.dataset.value = textValue || inputElement.value;
  }

  onEscapePress() {
    this.listNameInput.nativeElement.value = this.list?.name || '';
    this.isEscPressed = true;
    this.listNameInput.nativeElement.blur();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  onDeleteItems(): void {
    const options: SweetAlertOptions = { confirmButtonText: 'Delete', confirmButtonColor: '#dc3545' };

    if (this.selectedItemsSize > 1) {
      options.html = `Are you sure you want to delete the ${this.selectedItemsSize} selected items?`;
    }

    this.confirmService
      .confirm('deleteListItem', options)
      .then(() => {
        const items = Array.from(this.listItemSelectionService.selectedItems);
        this.listItemSelectionService.clearSelection();
        this.deleteItems.emit(items);
      })
      .catch(() => {
        // no-op
      });
  }

  onStartBulkApplyItemColour() {
    const modalRef = this.modal.open(BulkApplyListRowStyleModal, { windowClass: 'sm-flex sm-items-center' });
    modalRef.componentInstance.parent = {
      hierarchy: this.folder.hierarchy,
      id: this.folder.project?._id || this.folder.company?._id,
    };
    modalRef.componentInstance.listId = this.list._id;

    modalRef.result
      .then(({ fullList }) => {
        this.listItemStylesUpdated.emit({ fullList });
      })
      .catch(() => {
        // no op
      });
  }
}
